import { makeStyles } from "tss-react/mui"
import { Stack, TextField, Tooltip, Typography } from "@mui/material"

import { FilteredStatuses, MeetingServiceOrder, MeetingServiceOrderStatus } from "../meetingServiceTypes"
import { Box } from "@mui/system"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { OrdersStatusTable } from "./statusChangeModal/OrdersStatusTable"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/pro-light-svg-icons"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { batchUpdateDeliveryStatus, MerchantMeetingServiceOrdersQuery } from "../meetingServiceApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useRef } from "react"
import { enqueueSnackbar } from "notistack"
import { MerchantHomeQuery } from "../../../home/merchantHomeApi"
import { Order } from "../../../../controlKitchenTypes"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "2rem",
    },
    messageSection: {
        maxWidth: "50%",
    },
    warningIcon: {
        color: theme.palette.tailwind.red[500],
        marginRight: theme.spacing(1),
    },
    changeButton: {
        width: "fit-content",
        alignSelf: "center",
    },
    messageReason: {
        fontSize: "0.75rem",
        color: theme.palette.tailwind.slate[400],
    },
}))

type StartPageLayoutProps = Readonly<{
    setShowUndo: (ordersChanged: number) => void
    onClose: () => void
    orders: MeetingServiceOrder[] | Order[]
    selectedStatus: FilteredStatuses
    allowedOrderIds: number[]
}>

export function StatusChangeModal({
    orders,
    selectedStatus,
    allowedOrderIds,
    setShowUndo,
    onClose,
}: StartPageLayoutProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const fieldRef = useRef<HTMLInputElement>(null)
    const showMessageField = selectedStatus === MeetingServiceOrderStatus.REJECTED
    const isSomeOrderDisabled = orders.some(
        (order) => !!order.orderAction.statusTransitions?.disallowed?.[selectedStatus],
    )

    const {
        mutate: batchUpdate,
        data,
        isLoading,
        isSuccess,
    } = useApiMutation(batchUpdateDeliveryStatus, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS],
            })
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })

            const successCount = data?.orders?.filter((tx) => tx.status.code === 200).length ?? 0
            const failedCount = data?.orders?.filter((transaction) => transaction.status.code !== 200).length

            if (failedCount === 0) {
                setShowUndo(successCount)
                onClose()
            } else {
                enqueueSnackbar({
                    variant: "error",
                    message: l10n.getString("meeting-service-batch-modal-batch-error", {
                        ordersCount: failedCount,
                    }),
                    anchorOrigin: {
                        vertical: "top",
                        horizontal: "center",
                    },
                })
            }
        },
    })

    const handleBatchClick = () => {
        batchUpdate({
            updateDeliveryStatus: {
                orderIds: allowedOrderIds,
                status: selectedStatus,
                message: fieldRef?.current?.value,
            },
        })
    }

    return (
        <Stack direction="column" gap={7.5} className={classes.container}>
            {showMessageField && (
                <Box className={classes.messageSection}>
                    <LocalizedStrict id="meeting-service-batch-modal-customer-message">
                        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                            Message to customer
                        </Typography>
                    </LocalizedStrict>
                    <TextField
                        multiline
                        inputRef={fieldRef}
                        rows={2}
                        fullWidth
                        placeholder={l10n.getString("meeting-service-batch-modal-cancellation-reason-message")}
                        variant="outlined"
                    />
                </Box>
            )}

            <Box>
                {isSomeOrderDisabled && (
                    <Stack direction="row" alignItems="center">
                        <FontAwesomeIcon icon={faCircleExclamation} className={classes.warningIcon} />
                        <LocalizedStrict id="meeting-service-batch-modal-warning-message">
                            <Typography variant="body1">Could not change status for orders with this icon</Typography>
                        </LocalizedStrict>
                    </Stack>
                )}
                <OrdersStatusTable orders={orders} status={selectedStatus} batchResponse={data?.orders} />
            </Box>
            <LocalizedStrict id="meeting-service-batch-modal-button-close-tooltip-label" attrs={{ title: true }}>
                <Tooltip title="No orders can change status">
                    <Box className={classes.changeButton}>
                        <LocalizedStrict
                            id={
                                isSuccess
                                    ? "meeting-service-batch-modal-button-close-label"
                                    : "meeting-service-batch-modal-button-change-label"
                            }
                        >
                            <StandardButton
                                color="black"
                                fullWidth={false}
                                disabled={isLoading || !allowedOrderIds.length}
                                onClick={isSuccess ? onClose : handleBatchClick}
                            >
                                Confirm
                            </StandardButton>
                        </LocalizedStrict>
                    </Box>
                </Tooltip>
            </LocalizedStrict>
        </Stack>
    )
}
